import { useQuery } from '@apollo/client';
import { Download, ReceiptLong } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useExportAuditLogs } from '~/api/audit-logs/export';
import { NetworkAuditLogsDocument } from '~/components/AuditLogs/queries/list.generated';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { DownloadDialog, FilterAccordion, FilterChips, Table } from './components';
import { useFilters, useSelectedFilterParams } from './lib';

export const AuditLogList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { recordTypes: selectedRecordTypes, userIds: selectedUserIds } = useSelectedFilterParams();

  const [exportAuditLogs, { loading: exporting }] = useExportAuditLogs();
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const { data, loading } = useQuery(NetworkAuditLogsDocument, {
    variables: {
      networkId: currentNetwork.id,
      page,
      perPage,
      recordTypes: selectedRecordTypes,
      search,
      userIds: selectedUserIds,
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.auditLogs.pageInfo),
  });

  const auditUsers = data?.network?.userIds.nodes;
  const filterCounts = data?.network?.auditLogsFilterInfo;

  const { filters, clearFilters, setFilterItems, toggleAccordion, toggleItem, togglePanel } =
    useFilters(auditUsers);

  const panelOpen = filters.recordTypes.panelOpen || filters.userIds.panelOpen;

  const filtersActive = useMemo(
    () => selectedRecordTypes.length > 0 || selectedUserIds.length > 0,
    [selectedRecordTypes, selectedUserIds],
  );

  const totalCount = useTotalCount(data?.network?.auditLogs.pageInfo.nodesCount);

  const handleExport = async (startDate?: DateTime, endDate?: DateTime) => {
    const { data: exportData } = await exportAuditLogs({
      variables: {
        input: {
          networkId: currentNetwork.id,
          filters: {
            recordTypes: selectedRecordTypes.length > 0 ? selectedRecordTypes : undefined,
            search: search || undefined,
            userIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
            startDate: startDate?.toISO(),
            endDate: endDate?.toISO(),
          },
        },
      },
    });

    const url = exportData?.exportAuditLogs?.url;
    if (url) setExportUrl(url);
  };

  const handleCloseDialog = () => {
    setShowExportDialog(false);
    setExportUrl(null);
  };

  return (
    <>
      <Toolbar
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<ReceiptLong />}
        titleText={t('common.tabs.audit_logs')}
        actions={
          <Button
            color="primary"
            startIcon={<Download />}
            variant="contained"
            onClick={() => setShowExportDialog(true)}
            disabled={exporting || !totalCount}
          >
            {t('common.buttons.export')}
          </Button>
        }
      />
      <ListPageContainer>
        <FilterSidebar open={panelOpen} persistAs="auditLogs:filterWidth">
          <FilterAccordion
            clearFilter={clearFilters}
            filters={filters}
            filterCounts={filterCounts}
            toggleAccordion={toggleAccordion}
            updateFilter={setFilterItems}
          />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => {
                    togglePanel('recordTypes');
                    togglePanel('userIds');
                  }}
                />
                <SearchBar
                  onChange={(value) =>
                    setSearchParams((params) => {
                      params.set('search', value);
                      params.set('page', '1');
                      return params;
                    })
                  }
                  placeholder={t('pages.audit_logs.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center', gap: 2 }}>
                <Count
                  selectedCount={0}
                  totalCount={totalCount || 0}
                  thing="common.pluralize.audit_log_plural"
                />
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips filters={filters} toggleItem={toggleItem} />
              </Box>
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table auditLogs={data?.network?.auditLogs.nodes ?? []} loading={loading} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      {(showExportDialog || exportUrl) && (
        <DownloadDialog
          close={handleCloseDialog}
          exportUrl={exportUrl}
          onExport={handleExport}
          exporting={exporting}
          totalCount={totalCount}
          minYear={filterCounts?.minYear}
          maxYear={filterCounts?.maxYear}
        />
      )}
    </>
  );
};
